import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import Button from '../Button';
import { media } from '../../styles/media';
import Text from '../Typography/Text';
import theme from '../../theme';
import Link from '../Link/Link';

const Wrapper = styled.div`
  max-width: ${({ maxWidth }) => maxWidth || 305}px;
  min-width: ${({ minWidth }) => minWidth || 305}px;
  position: relative;
  height: ${({ fullHeight }) => (fullHeight ? '100%' : 'auto')};

  .card-image {
    transition: transform 1s ease;
  }

  &:hover {
    cursor: pointer;

    .card-image {
      transform: scale(1.1);
    }
  }
`;

const ButtonsWrapper = styled.div`
  display: inline-flex;
  flex-direction: column;
  margin-top: 10px;

  ${media.tablet`
    flex-direction: row;
    align-items: center;
  `};
`;

const StyledButton = styled(Button)`
  margin-bottom: 20px;

  ${media.tablet`
    margin-bottom: 0px;
    margin-right: 60px;
  `};
`;

const ImageWrapper = styled.div`
  overflow: hidden;
  position: relative;
  width: ${({ width }) => width || 305}px;
  height: ${({ height }) => height || 305}px;

  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 37.85%,
      rgba(0, 0, 0, 0.5) 72.6%
    );
  }
`;

const Image = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;

  transition: transform 0.3s ease-in-out;
`;

const Card = ({ card, maxWidth, minWidth, fullHeight }) => {
  const { title, description, date, image, link, ctaLeft, ctaRight } = card;

  return (
    <Link href={link}>
      <Wrapper maxWidth={maxWidth} minWidth={minWidth} fullHeight={fullHeight}>
        {image?.url && (
          <ImageWrapper width={image?.width} height={image?.height}>
            <Image src={image?.url} alt={image?.alt} className="card-image" />
          </ImageWrapper>
        )}

        {date && (
          <Text
            variant="date"
            color={theme.color.brand.accent}
            marginTop="20px"
            transform="uppercase"
          >
            {date}
          </Text>
        )}

        {title && (
          <Text
            variant="heading3"
            color={theme.color.brand.primary}
            as="h3"
            marginTop="10px"
          >
            {title}
          </Text>
        )}

        {description && (
          <Text variant="body2" color={theme.color.brand.grey} marginTop="10px">
            {description}
          </Text>
        )}

        <ButtonsWrapper>
          {ctaLeft?.label && (
            <StyledButton iconType={ctaLeft?.iconType} href={ctaLeft?.url}>
              {ctaLeft?.label}
            </StyledButton>
          )}

          {ctaRight?.label && (
            <Button iconType={ctaRight?.iconType} href={ctaRight?.url}>
              {ctaRight?.label}
            </Button>
          )}
        </ButtonsWrapper>
      </Wrapper>
    </Link>
  );
};

Card.propTypes = {
  card: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    date: PropTypes.string,
    image: PropTypes.shape({
      url: PropTypes.string,
      alt: PropTypes.string,
      height: PropTypes.number,
      width: PropTypes.number
    }),
    link: PropTypes.string,
    ctaLeft: PropTypes.shape({
      label: PropTypes.string,
      iconType: PropTypes.string,
      url: PropTypes.string
    }),
    ctaRight: PropTypes.shape({
      label: PropTypes.string,
      iconType: PropTypes.string,
      url: PropTypes.string
    })
  }),
  maxWidth: PropTypes.number,
  minWidth: PropTypes.number,
  fullHeight: PropTypes.bool
};

export default Card;
